import { useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import type {
  StudioSession,
  StudioState,
  SupportedLang,
} from "@acme/validators";

import { useTRPC } from "~/trpc/react";
import { useSession } from "~/utils/auth";
import { useStudioStore } from "./use-studio-store";

export function useSessions() {
  const store = useStudioStore();
  const api = useTRPC();
  const queryClient = useQueryClient();

  const userSession = useSession();

  // Fetch session list
  const {
    data: sessionsData,
    isLoading: isLoadingSessions,
    error: sessionsError,
  } = useQuery(
    api.astGrepStudioSession.list.queryOptions(
      {
        page: 1,
        limit: 40,
      },
      {
        enabled: userSession.status === "authenticated",
      },
    ),
  );

  // Fetch current session if selected
  const { data: currentSessionData, isLoading: isLoadingSession } = useQuery(
    api.astGrepStudioSession.get.queryOptions(
      { id: store.currentSessionId ?? "" },
      { enabled: !!store.currentSessionId },
    ),
  );

  // Load session data into store when it's fetched
  useEffect(() => {
    if (
      currentSessionData &&
      store.currentSessionId !== currentSessionData.id
    ) {
      // The types don't match exactly
      // with the store types, but the values are compatible
      store.loadSessionState({
        ...currentSessionData.state,
        lang: currentSessionData.lang as SupportedLang,
      });
    }
  }, [currentSessionData, store]);

  // Create a new session
  const createSessionMutation = useMutation(
    api.astGrepStudioSession.create.mutationOptions({
      onSuccess: (data) => {
        toast.success("Session created");
        void queryClient.invalidateQueries({
          queryKey: api.astGrepStudioSession.list.queryKey(),
        });
        store.setCurrentSessionId(data?.id ?? null);
        store.setDirty(false);
      },
      onError: (error) => {
        toast.error(`Failed to create session: ${error.message}`);
      },
    }),
  );

  const createSession = useCallback(
    async (name: string, lang: SupportedLang, state: StudioState) => {
      try {
        const result = await createSessionMutation.mutateAsync({
          name,
          lang,
          state,
        });

        // TODO: Optimistically update the store

        return result;
      } catch (error) {
        console.error("Error creating session:", error);
        throw error;
      }
    },
    [createSessionMutation],
  );

  // Update an existing session
  const updateSessionMutation = useMutation(
    api.astGrepStudioSession.update.mutationOptions({
      onSuccess: () => {
        toast.success("Session updated");
        if (store.currentSessionId) {
          void queryClient.invalidateQueries({
            queryKey: api.astGrepStudioSession.get.queryKey({
              id: store.currentSessionId,
            }),
          });
          store.setCurrentSessionId(store.currentSessionId);
          store.setDirty(false);
        }
      },
      onError: (error) => {
        toast.error(`Failed to update session: ${error.message}`);
      },
    }),
  );

  const updateSession = useCallback(
    async (id: string, data: Partial<StudioSession>) => {
      try {
        // Only update the session if it's the current one
        if (id === store.currentSessionId) {
          await updateSessionMutation.mutateAsync({
            id,
            name: data.name,
            state: data.state,
          });
        }
      } catch (error) {
        console.error("Error updating session:", error);
        throw error;
      }
    },
    [store.currentSessionId, updateSessionMutation],
  );

  // Delete a session
  const deleteSessionMutation = useMutation(
    api.astGrepStudioSession.delete.mutationOptions({
      onSuccess: () => {
        toast.success("Session deleted");
        void queryClient.invalidateQueries({
          queryKey: api.astGrepStudioSession.list.queryKey(),
        });
      },
      onError: (error) => {
        toast.error(`Failed to delete session: ${error.message}`);
      },
    }),
  );

  const deleteSession = useCallback(
    async (id: string) => {
      try {
        await deleteSessionMutation.mutateAsync({ id });
        if (id === store.currentSessionId) {
          store.setCurrentSessionId(null);
        }
      } catch (error) {
        console.error("Error deleting session:", error);
        throw error;
      }
    },
    [deleteSessionMutation, store],
  );

  // Current session in local format
  const currentSession: StudioSession | null = currentSessionData
    ? {
        id: currentSessionData.id,
        name: currentSessionData.name ?? "",
        state: currentSessionData.state,
        createdAt: new Date(currentSessionData.createdAt ?? new Date()),
        updatedAt: new Date(currentSessionData.updatedAt),
      }
    : null;

  return {
    sessions: sessionsData?.data ?? [],
    currentSession,
    isLoadingSessions,
    isLoadingSession,
    isCreatingSession: createSessionMutation.isPending,
    isUpdatingSession: updateSessionMutation.isPending,
    isDeletingSession: deleteSessionMutation.isPending,
    sessionsError,
    createSession,
    updateSession,
    deleteSession,
  };
}
